import {
  type AutocompleteRenderInputParams,
  Box,
  InputAdornment,
  TextField,
} from '@mui/material';
import {fuzzyMatchHighlight} from '@wandb/weave/common/util/fuzzyMatch';
import {Button} from '@wandb/weave/components/Button';
import {AutoComplete} from '@wandb/weave/components/Form/AutoComplete';
import {
  IconClose,
  IconRecentClock,
  IconSearch,
} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useIntersection} from 'react-use';

import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import {useColorMode} from '../../../util/useColorMode';
import type {
  BaseSearchHistoryItem,
  OptionTypeBase,
  RunSearchHistoryItem,
} from '../types';
import {trackSearchHistory} from './searchHistoryEvents';

const TEXTAREA_MIN_ROWS_HEIGHT = 32;
const INTERSECTION_THRESHOLDS = [0, 1];
type SearchInputElement = HTMLInputElement | HTMLTextAreaElement;

interface SearchHistoryProps<
  BaseSearchHistoryItem,
  TOption extends OptionTypeBase,
> {
  searchHistory: BaseSearchHistoryItem[];
  onOptionChange: (value: TOption | string) => void;
  onFocus?: () => void;
  onBlur: (inputValue: string) => void;
  setInputValue:
    | React.Dispatch<React.SetStateAction<string>>
    | ((newSearchQuery: string) => void);
  inputValue: string;
  customActionButton?: React.JSX.Element;
  placeholderText: string;
  // Stable Datadog RUM action name (data-dd-action-name) for the input. Kept
  // separate from placeholderText so it doesn't change when the placeholder does.
  ddActionName: string;
  perfAction?: string;
  // Lets owners capture native edit friction before the value changes, e.g.
  // paste, deletion, or replacement, without teaching this shared component
  // metric semantics. This intentionally uses the browser beforeinput event
  // instead of React's synthetic onBeforeInput, which does not expose deletion
  // as an inputType in React 19.
  onNativeBeforeInput?: (event: InputEvent, input: SearchInputElement) => void;
  onInteraction?: (event: React.SyntheticEvent) => void;
  onClear?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  maxWidth?: number;
  selectedOption: TOption | null;
  setSelectedOption: React.Dispatch<React.SetStateAction<TOption | null>>;
  minWidth?: number;
  isCollapsible?: boolean;
  maxItems: number;
  focusOnPageLoad?: boolean;
  dataTest?: string;
  error?: boolean;
  errorMessage?: React.ReactNode;
  /** Closes the popup when the input is clipped by this scrolling viewport. */
  scrollRoot?: Element | null;
}

// TODO: Consolidate this with components/SearchHistory.tsx
export const SearchHistory = <
  T extends BaseSearchHistoryItem,
  TOption extends OptionTypeBase,
>({
  searchHistory,
  onOptionChange,
  onFocus,
  onBlur,
  inputValue,
  setInputValue,
  customActionButton,
  placeholderText,
  ddActionName,
  perfAction,
  onNativeBeforeInput,
  onInteraction,
  onClear,
  maxWidth,
  minWidth = 100,
  selectedOption,
  setSelectedOption,
  isCollapsible,
  maxItems,
  focusOnPageLoad,
  dataTest,
  error,
  errorMessage,
  scrollRoot,
}: SearchHistoryProps<T, TOption>) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [autocompleteWidth, setAutocompleteWidth] = useState<number>(0);

  const isInitialAutoFocus = useRef(focusOnPageLoad);
  const inputRef = useRef<SearchInputElement | null>(null);
  const nativeBeforeInputCleanupRef = useRef<(() => void) | null>(null);
  // Blur can fire before the controlled inputValue prop re-renders.
  const latestInputValueRef = useRef(inputValue);
  const {isDarkMode} = useColorMode();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement | null>(null);
  const intersection = useIntersection(
    autocompleteRef as React.RefObject<HTMLElement>,
    {
      root: scrollRoot ?? null,
      threshold: INTERSECTION_THRESHOLDS,
    }
  );
  const isClipped =
    intersection != null &&
    (!intersection.isIntersecting ||
      intersection.boundingClientRect.width >
        intersection.intersectionRect.width ||
      intersection.boundingClientRect.height >
        intersection.intersectionRect.height);

  useEffect(() => {
    // Reset isInitialAutoFocus on page load to prevent it opening after switching tabs
    isInitialAutoFocus.current = focusOnPageLoad;
  }, [focusOnPageLoad]);

  useEffect(() => {
    latestInputValueRef.current = inputValue;
  }, [inputValue]);

  const setInputRef = useCallback(
    (node: SearchInputElement | null) => {
      nativeBeforeInputCleanupRef.current?.();
      nativeBeforeInputCleanupRef.current = null;
      inputRef.current = node;
      if (node == null || onNativeBeforeInput == null) {
        return;
      }

      const handleBeforeInput = (event: Event) => {
        onNativeBeforeInput(event as InputEvent, node);
      };

      node.addEventListener('beforeinput', handleBeforeInput);
      nativeBeforeInputCleanupRef.current = () => {
        node.removeEventListener('beforeinput', handleBeforeInput);
      };
    },
    [onNativeBeforeInput]
  );

  useEffect(() => {
    if (!isCollapsible || !inputRef.current) {
      return;
    }

    const nextIsOverflowing =
      inputRef.current.scrollHeight > TEXTAREA_MIN_ROWS_HEIGHT;
    setIsOverflowing(nextIsOverflowing);
  }, [isCollapsible, isExpanded, inputValue]);

  useEffect(() => {
    const autocompleteNode = autocompleteRef.current;
    if (!autocompleteNode) {
      return;
    }

    const handleResize = () => {
      setAutocompleteWidth(autocompleteNode.offsetWidth);
    };

    handleResize();

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(autocompleteNode);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !isClipped) {
      return;
    }

    setIsOpen(false);
    inputRef.current?.blur();
  }, [isClipped, isOpen]);

  const getLabel = useCallback(
    (dropdownValue: string) => {
      const match = fuzzyMatchHighlight(
        dropdownValue,
        inputValue,
        undefined,
        true
      );
      return (
        <Tailwind>
          <div className="flex items-start">
            <div className="moon-800 mr-8 mt-3 flex-shrink-0">
              <IconRecentClock width={18} height={18} />
            </div>
            <div className="line-clamp-5 break-all">
              {match == null || selectedOption !== null ? dropdownValue : match}
            </div>
          </div>
        </Tailwind>
      );
    },
    [inputValue, selectedOption]
  );

  const searchHistoryItems: TOption[] = searchHistory
    .filter(s => s.query !== inputValue)
    .slice(0, maxItems)
    .map(s => ({
      value: s.query,
      label: s.query,
      ...(isRunSearchHistoryItem(s) ? {isRegex: s.isRegex} : {}),
    })) as TOption[];

  const handleInputChange = useCallback(
    (newInputValue: string, reason: string) => {
      if (reason === 'reset') {
        return;
      }
      latestInputValueRef.current = newInputValue;
      setInputValue(newInputValue);
      setSelectedOption(null);
      setIsOpen(true);
    },
    [setInputValue, setSelectedOption, setIsOpen]
  );

  const handleOptionChange = useCallback(
    (newValue: TOption | string | null) => {
      if (!newValue) {
        return;
      }

      onOptionChange(newValue);
      if (typeof newValue !== 'string') {
        latestInputValueRef.current = newValue.value;
        setInputValue(newValue.value);
      }

      setIsOpen(false);
      inputRef.current?.blur();
    },
    [onOptionChange, setInputValue, setIsOpen, inputRef]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      onInteraction?.(event);
      if (event.key === 'Escape' || event.key === 'Enter') {
        setIsOpen(false);
      }
      if (event.key === 'Enter') {
        // Blur the input to remove focus
        if (inputRef.current) {
          inputRef.current.blur();
        }
      }
    },
    [onInteraction, setIsOpen, inputRef]
  );

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
        return;
      }

      // NOTE: flipping isExpanded swaps the MUI input from <input> to
      // <textarea>, which drops focus from the original node. Only re-focus
      // on the swap; refocusing on every event resets the caret blink.
      const willSwapToMultiline = isCollapsible && !isExpanded;

      setIsExpanded(true);
      // Prevent the menu from opening on the first focus (autofocus)
      if (isInitialAutoFocus.current) {
        isInitialAutoFocus.current = false;
      } else {
        setIsOpen(true);
      }

      if (willSwapToMultiline) {
        requestAnimationFrame(() => {
          inputRef.current?.focus();
        });
      }

      onFocus?.();
    },
    [isCollapsible, isExpanded, onFocus, isInitialAutoFocus, setIsOpen]
  );

  const onClearHandler = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onInteraction?.(event);
      event.stopPropagation();
      setIsOpen(false);
      onClear?.(event);
      setSelectedOption(null);
      latestInputValueRef.current = '';
      setInputValue('');
      if (inputRef.current) {
        inputRef.current.blur();
      }
    },
    [onClear, onInteraction, setSelectedOption, setInputValue]
  );

  const handleAutocompleteInputChange = useCallback(
    (event: React.SyntheticEvent, newValue: string, reason: string) => {
      if (reason !== 'reset') {
        onInteraction?.(event);
      }
      handleInputChange(newValue, reason);
    },
    [handleInputChange, onInteraction]
  );

  const handleAutocompleteChange = useCallback(
    (event: React.SyntheticEvent, newValue: unknown) => {
      onInteraction?.(event);
      handleOptionChange(newValue as TOption | string | null);
    },
    [handleOptionChange, onInteraction]
  );

  const isOptionEqualToValue = useCallback(
    (option: TOption, value: string | TOption) =>
      option.value === (typeof value === 'string' ? value : value.value),
    []
  );

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: TOption) => (
      // The listbox is portaled out of the wrapper below, so it can't inherit
      // its data-dd-* attrs. Set a stable name here or RUM derives the action
      // from the option's dynamic text (the user's search query/regex).
      <Box
        component="li"
        {...props}
        data-perf-action={perfAction}
        data-dd-privacy={DD_PRIVACY_ALLOW}
        data-dd-action-name="Search history option">
        {getLabel(option.value)}
      </Box>
    ),
    [getLabel, perfAction]
  );

  const textFieldSx = useMemo(
    () => ({
      '.MuiInputBase-root': {
        alignItems: 'flex-start',
      },
      '.MuiInputAdornment-root': {
        alignItems: 'flex-start',
        marginTop: '4px',
      },
      '.MuiAutocomplete-endAdornment': {
        top: '0%',
        transform: 'translate(0, 0%) !important',
        alignItems: 'flex-start !important',
        marginTop: '4px',
      },
      textarea: {
        resize: 'none',
        lineHeight: isExpanded && !isOverflowing ? '32px' : '160%', // when input changes to multiline, the text is offcenter
        marginY: isExpanded && isOverflowing ? '4px' : '0px',
      },
      '.MuiFormHelperText-root': {
        fontFamily: 'Source Sans Pro',
        fontSize: '14px',
        marginLeft: 0,
      },
    }),
    [isExpanded, isOverflowing]
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      const inputSlotProps = {
        ...params.slotProps,
        htmlInput: {
          ...params.slotProps.htmlInput,
          'data-perf-action': perfAction,
        },
        input: {
          ...params.slotProps.input,
          startAdornment: (
            <IconSearch
              color="var(--wb-text-tertiary)"
              style={{
                flexShrink: 0,
                marginTop: '6px',
                marginRight: '4px',
              }}
            />
          ),
          endAdornment: autocompleteWidth >= minWidth && (
            <>
              <InputAdornment position="end">
                {customActionButton}
              </InputAdornment>
              <div className="flex flex-col">
                <InputAdornment position="end">
                  {inputValue ? params.slotProps.input.endAdornment : null}
                </InputAdornment>
                {isOverflowing && isExpanded && inputValue !== '' && (
                  <Button
                    data-perf-action={perfAction}
                    size="small"
                    variant="ghost"
                    icon="expand-right"
                    className="absolute bottom-6 right-7 -rotate-90"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      event.stopPropagation();
                      setIsExpanded(false);
                      inputRef?.current?.blur();
                      trackSearchHistory({
                        action: 'Collapsed input',
                        location: 'workspace run selector',
                        inputLength: inputValue.length,
                        inputHeight: inputRef.current?.offsetHeight,
                      });
                    }}
                  />
                )}
              </div>
            </>
          ),
        },
      };

      return (
        <TextField
          autoFocus={focusOnPageLoad}
          data-test={dataTest}
          error={error}
          helperText={error ? errorMessage : undefined}
          multiline={isCollapsible ? isExpanded : false}
          minRows={1}
          maxRows={6}
          {...params}
          inputRef={setInputRef}
          variant="outlined"
          placeholder={placeholderText}
          sx={textFieldSx}
          slotProps={inputSlotProps}
        />
      );
    },
    [
      focusOnPageLoad,
      perfAction,
      dataTest,
      error,
      errorMessage,
      isCollapsible,
      isExpanded,
      placeholderText,
      textFieldSx,
      autocompleteWidth,
      minWidth,
      customActionButton,
      inputValue,
      isOverflowing,
      setInputRef,
    ]
  );

  const autocompleteSlotProps = useMemo(
    () => ({
      clearIndicator: {
        'data-perf-action': perfAction,
        onClick: onClearHandler,
        component: IconClose,
        style: {
          visibility: inputValue ? ('visible' as const) : ('hidden' as const),
        },
      },
    }),
    [onClearHandler, inputValue, perfAction]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
        return;
      }

      setIsOpen(false);
      onBlur(latestInputValueRef.current);
    },
    [onBlur]
  );

  return (
    <div
      data-dd-privacy={DD_PRIVACY_ALLOW}
      data-dd-action-name={ddActionName}
      style={{width: '100%'}}>
      <AutoComplete<TOption>
        autocompleteRef={autocompleteRef}
        freeSolo
        openOnFocus={false}
        open={isOpen}
        isDarkMode={isDarkMode}
        maxWidth={maxWidth}
        minWidth={minWidth}
        value={selectedOption ?? ''}
        inputValue={inputValue}
        onInputChange={handleAutocompleteInputChange}
        onChange={handleAutocompleteChange}
        options={searchHistoryItems}
        onKeyDown={handleKeyDown}
        hasInputValue={inputValue !== ''}
        isOptionEqualToValue={isOptionEqualToValue}
        renderOption={renderOption}
        noOptionsText={null}
        renderInput={renderInput}
        slotProps={autocompleteSlotProps}
        fullWidth
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    </div>
  );
};

export function isRunSearchHistoryItem(
  item: BaseSearchHistoryItem
): item is RunSearchHistoryItem {
  return (item as RunSearchHistoryItem).isRegex !== undefined;
}
